/**
 * SlashCommand — `/` suggestion extension that opens a cmdk palette of
 * block types. Picking an item runs the corresponding TipTap command
 * (set heading, wrap in bullet list, insert todo, etc.).
 *
 * Trigger: `/` at the start of a line, or after whitespace. We use
 * `startOfLine: false` so the user can also fire it mid-line (Notion
 * behaves the same way).
 */

import { Extension } from '@tiptap/core'
import { Suggestion, type SuggestionOptions } from '@tiptap/suggestion'
import { ReactRenderer } from '@tiptap/react'
import { PluginKey } from '@tiptap/pm/state'
import { mountSuggestion, unmountSuggestion } from './suggestionRenderer'
import { SlashMenu, type SlashMenuRef, type SlashItem } from '@/components/notebook/SlashMenu'

/**
 * Each `Suggestion({...})` defaults to the shared `SuggestionPluginKey`
 * singleton. With three suggestion-using extensions in the editor
 * (slash, mention, page-link) we'd collide on that key and ProseMirror
 * throws "Adding different instances of a keyed plugin (suggestion$)".
 * We give each one a unique PluginKey instead.
 */
const SlashCommandPluginKey = new PluginKey('slash-command')


export const defaultSlashItems: SlashItem[] = [
  { key: 'p', title: 'Text', description: 'Plain paragraph', run: (chain) => chain.setParagraph() },
  { key: 'h1', title: 'Heading 1', description: 'Section title', run: (chain) => chain.toggleHeading({ level: 1 }) },
  { key: 'h2', title: 'Heading 2', description: 'Sub-section', run: (chain) => chain.toggleHeading({ level: 2 }) },
  { key: 'h3', title: 'Heading 3', description: 'Sub-sub-section', run: (chain) => chain.toggleHeading({ level: 3 }) },
  { key: 'ul', title: 'Bulleted list', description: 'Unordered list', run: (chain) => chain.toggleBulletList() },
  { key: 'ol', title: 'Numbered list', description: 'Ordered list', run: (chain) => chain.toggleOrderedList() },
  { key: 'todo', title: 'Todo list', description: 'Checkboxes (- [ ])', run: (chain) => chain.toggleTaskList() },
  { key: 'quote', title: 'Quote', description: 'Block quote', run: (chain) => chain.toggleBlockquote() },
  { key: 'code', title: 'Code block', description: 'Fenced code', run: (chain) => chain.toggleCodeBlock() },
  { key: 'tbl', title: 'Table', description: '3 × 3 table with a header row', run: (chain) => chain.insertTableWithText({ rows: 3, cols: 3, withHeaderRow: true }) },
  { key: 'hr', title: 'Divider', description: 'Horizontal rule', run: (chain) => chain.setHorizontalRule() },
]

export interface SlashCommandOptions {
  items?: SlashItem[]
}

export const SlashCommand = Extension.create<SlashCommandOptions>({
  name: 'slashCommand',
  addOptions() {
    return { items: defaultSlashItems }
  },
  addProseMirrorPlugins() {
    const items: SlashItem[] = this.options.items ?? defaultSlashItems
    return [
      Suggestion({
        editor: this.editor,
        pluginKey: SlashCommandPluginKey,
        char: '/',
        startOfLine: false,
        allowSpaces: false,
        items: ({ query }) => {
          const q = query.toLowerCase()
          return items.filter(
            (i) =>
              i.title.toLowerCase().includes(q) ||
              i.key.toLowerCase().includes(q) ||
              (i.description ?? '').toLowerCase().includes(q),
          )
        },
        command: ({ editor, range, props }) => {
          const item = props as SlashItem
          editor.chain().focus().deleteRange(range).run()
          // The item queues its command on the chain; the extension runs
          // the chain once, so an item must not call `.run()` itself.
          const chain = editor.chain().focus()
          item.run(chain as never)
          chain.run()
        },
        render: () => {
          let component: ReactRenderer<SlashMenuRef> | null = null
          return {
            onStart: (props) => {
              component = new ReactRenderer(SlashMenu, {
                props,
                editor: props.editor,
              })
              mountSuggestion(component)
            },
            onUpdate: (props) => {
              component?.updateProps(props)
            },
            onKeyDown: (props) => {
              if (props.event.key === 'Escape') {
                return true
              }
              return component?.ref?.onKeyDown(props) ?? false
            },
            onExit: () => {
              unmountSuggestion(component)
              component = null
            },
          }
        },
      }),
    ]
  },
})
